import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";

import {
  GetCustomerMemberships,
  DeleteMembership,
  DisableMembership,
  PostCustomerMembership,
  MoveMembership,
  PrintMembership,
  EmailMembership,
  PostSuspendMembership,
  PutUnsuspendMembership,
  EnableMembership,
  PutEditExpiryDate,
} from "api/rpc/2024-04/facilityAdmin/customer/membership";

import { showError, showSuccess } from "redux/actions/ui";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useCustomerContext } from "../../context/CustomerContext";
import { CustomerMembershipsType, TicketStubType } from "../../context/contextTypes";

import Input from "components/form/input/Input";
import Spin from "components/spin/spin";
import { ButtonNew as Button } from "components/buttonNew";
import NewCustomerMembershipModal from "elements/customer/membership/NewCustomerMembershipModal";
import MembershipDisplay from "elements/customer/membership/MembershipDisplay";
import Sheet from "components/sheet/Sheet";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import { Select } from "components/select/index";
import { ICustomer } from "redux/reducers/models/customer";

import "./CustomerMemberships.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popup from "components/popup/Popup";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { GetTicketStub } from "api/rpc/2024-04/facilityAdmin/ticket/stub";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import MembershipDetailsSheet from "components/membershipDetailsSheet/MembershipDetailsSheet";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Checkbox from "components/form/checkbox/Checkbox";
import { formatDate } from "helpers/Helpers";
import { GetMembership } from "api/rpc/2024-04/facilityAdmin/membership/membership";
import useFutureBookingPricingModal from "../../futureBookingPricingModal/useFutureBookingPricingModal";
import { didCartCustomerTypeIdChange } from "../../futureBookingPricingModal/futureBookingPricingUtils";

interface IDisableMembershipState {
  showDisableMembership: boolean;
  membershipId: number;
}

interface ISuspendMembershipState {
  showSuspendMembership: boolean;
  openConfirmSuspension: boolean;
  indefinite_suspension: boolean;
  membership: CustomerMembershipsType;
  suspension_reason: string;
}
interface IDeleteMembershipState {
  showDeleteMembership: boolean;
  membershipIdToDelete: number;
}

interface IMoveMembershipState {
  showMoveMembership: boolean;
  showWarning: boolean;
  membershipId: number;
  customerQuery: string;
  customerSearching: boolean;
  customerSearchResult: Array<ICustomer>;
  selectedCustomer: ICustomer;
  currentCustomer: ICustomer;
}

interface ISelectedMembershipState {
  modalOpen: boolean;
  membership: CustomerMembershipsType;
}

interface IUnsuspendState {
  modalOpen: boolean;
  membership: CustomerMembershipsType;
}

interface IEditExpiryDateState {
  modalOpen: boolean;
  membership: CustomerMembershipsType;
  expiryDate: Date;
}

interface IEnableMembershipState {
  showEnableMembership: boolean;
  membershipId: number;
}

export default function CustomerMemberships() {
  const { t } = useTranslation();

  const { Option } = Select;

  const { state, updateState, loadCustomer } = useCustomerContext();
  const { openIfNeeded: openFutureBookingPricingModalIfNeeded, modal: futureBookingPricingModal } =
    useFutureBookingPricingModal({
      onSuccess: () => void loadCustomer(state.customer.id),
    });

  const [selectedMembership, setSelectedMembership] = useState<ISelectedMembershipState>({
    modalOpen: false,
    membership: null,
  });

  const [disableState, updateDisableState] = useState<IDisableMembershipState>({
    showDisableMembership: false,
    membershipId: null,
  });

  const [enableState, updateEnabledState] = useState<IEnableMembershipState>({
    showEnableMembership: false,
    membershipId: null,
  });

  const [deleteState, updateDeleteState] = useState<IDeleteMembershipState>({
    showDeleteMembership: false,
    membershipIdToDelete: null,
  });

  const [unsuspendState, setUnsuspendState] = useState<IUnsuspendState>({
    modalOpen: false,
    membership: null,
  });

  const [editExpiryDateState, updateEditExpiryDateState] = useState<IEditExpiryDateState>({
    modalOpen: false,
    membership: null,
    expiryDate: undefined,
  });

  const [suspendState, updateSuspendState] = useState<ISuspendMembershipState>({
    showSuspendMembership: false,
    openConfirmSuspension: false,
    indefinite_suspension: false,
    membership: null,
    suspension_reason: "",
  });

  const [moveState, updateMoveState] = useState<IMoveMembershipState>({
    showMoveMembership: false,
    showWarning: false,
    membershipId: null,
    customerQuery: "",
    customerSearching: false,
    customerSearchResult: null,
    selectedCustomer: null,
    currentCustomer: state.customer,
  });

  const [date, setDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());

  const dispatch = useAppDispatch();
  const authStore = useAppSelector(state => state.authStore);

  const permissions = authStore.user?.permissions;
  const windowSize = useWindowSize();

  const activeMemberships = state.memberships?.filter(membership => membership.status === "active");
  const pastMemberships = state.memberships?.filter(
    membership =>
      membership.status === "disabled" || membership.status === "suspended" || membership.status === "expired",
  );

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (state.memberships === null) {
      void loadCustomerMemberships(state.customer.id, true, source.token);
    }

    if (state.ticketStubs === null) {
      void loadTicketStubs(state.customer.id, source.token);
    }

    return () => {
      source.cancel(t("Customer Memberships unmounted."));
    };
  }, [state.customer.id]);

  async function loadCustomerMemberships(customerId: number, useGlobalLoader?: boolean, token?: CancelToken) {
    const membershipRes = await GetCustomerMemberships(
      { customer_id: customerId, parent_membership: true },
      useGlobalLoader ?? false,
      token,
    );

    console.log("membership res", membershipRes);
    if (membershipRes.status !== StatusCode.OK) {
      console.log(t("Error obtaining customer memberships: "), token.reason.message);
      return;
    }

    updateState({ memberships: membershipRes.data });
  }

  console.log("state", state);

  async function loadTicketStubs(customerId: number, token?: CancelToken, useGlobalLoader?: boolean) {
    const params = {
      customer_id: customerId,
      extended: true,
    };

    const ticketStubRes = await GetTicketStub(params, useGlobalLoader ?? false, token);
    if (ticketStubRes.status !== StatusCode.OK) {
      return;
    }

    const tempTicketStubsList: any = [];

    const sortedTicketStubs: Array<TicketStubType> = ticketStubRes.data.sort((a: TicketStubType, b: TicketStubType) => {
      return a.ticket_id - b.ticket_id;
    });

    const uniqueTicketIds = [...new Set(sortedTicketStubs.map((item: { ticket_id: number }) => item.ticket_id))];

    uniqueTicketIds.map((ticketId: number) => {
      const tempSortedTicket: any[] = [];

      sortedTicketStubs.map((sortedStub: any) => {
        if (sortedStub.ticket_id === ticketId) {
          tempSortedTicket.push(sortedStub);
        }
      });

      tempTicketStubsList.push(tempSortedTicket);
    });

    updateState({ ticketStubs: tempTicketStubsList });
  }

  async function postMembershipToCustomer(customerId: number, selectedMembershipId: number) {
    const res = await PostCustomerMembership(
      {
        customer_id: customerId,
        membership_id: selectedMembershipId,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error applying membership")));
      return;
    }

    void loadCustomerMemberships(state.customer.id, true);

    const membershipRes = await GetMembership({ id: selectedMembershipId }, false);
    const membership = membershipRes.data?.[0];

    if (membershipRes.status === StatusCode.OK && membership?.customer_type_id) {
      if (membership.membership_type === "membership") {
        await openFutureBookingPricingModalIfNeeded({
          customerId,
          customerTypeId: membership.customer_type_id,
        });
      } else if (
        membership.membership_type === "add_on" &&
        didCartCustomerTypeIdChange(state.customer.cart_customer_type_id, membership.customer_type_id)
      ) {
        await openFutureBookingPricingModalIfNeeded({
          customerId,
          cartCustomerTypeId: membership.customer_type_id,
        });
      }
    }
  }

  async function handleDisableMembership() {
    const disableMembershipRes = await DisableMembership({ membership_id: disableState.membershipId }, true);

    if (disableMembershipRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error disabling membership")));
      updateDisableState(prevState => ({ ...prevState, showDisableMembership: false, membershipId: null }));

      return;
    }

    updateDisableState(prevState => ({ ...prevState, showDisableMembership: false, membershipId: null }));

    const source = axios.CancelToken.source();
    void loadCustomerMemberships(state.customer.id, true, source.token);
  }

  async function handleUnsuspendMembership() {
    const params = {
      membership_id: unsuspendState?.membership.id,
    };

    const suspensionRes = await PutUnsuspendMembership(params, true);
    if (suspensionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error unsuspending selected membership")));
    }

    setUnsuspendState({ modalOpen: false, membership: null });
    void loadCustomerMemberships(state.customer.id, true);
    void loadCustomer(state.customer.id);
  }

  useEffect(() => {
    if (suspendState?.indefinite_suspension) {
      setDate(new Date(2500, 12, 24));
      setEndDate(new Date(2500, 12, 24));
    } else {
      setDate(new Date());
      setEndDate(new Date());
    }
  }, [suspendState?.indefinite_suspension]);

  async function handleSuspendMembership() {
    const params = {
      membership_id: suspendState?.membership.id,
      start_date: formatDate(date),
      end_date: formatDate(endDate),
      suspension_reason: suspendState?.suspension_reason,
      indefinite_suspension: suspendState?.indefinite_suspension,
    };

    const suspensionRes = await PostSuspendMembership(params, true);
    if (suspensionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error occurred when suspending membership")));
    }

    dispatch(showSuccess(t("Membership successfully suspended")));
    updateSuspendState({
      showSuspendMembership: false,
      openConfirmSuspension: false,
      membership: null,
      suspension_reason: "",
      indefinite_suspension: false,
    });

    void loadCustomerMemberships(state.customer.id, true);
    void loadCustomer(state.customer.id);
  }

  function handleDeleteMembershipPopup(membership_id: number) {
    updateDeleteState(prevState => ({
      ...prevState,
      showDeleteMembership: true,
      membershipIdToDelete: membership_id,
    }));
  }

  async function handleDeleteMembership() {
    const deleteMembershipRes = await DeleteMembership({ id: deleteState.membershipIdToDelete }, true);

    if (deleteMembershipRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting membership")));
      updateDeleteState(prevState => ({ ...prevState, showDeleteMembership: false, membershipIdToDelete: null }));

      return;
    }

    updateDeleteState(prevState => ({ ...prevState, showDeleteMembership: false, membershipIdToDelete: null }));

    const source = axios.CancelToken.source();
    void loadCustomerMemberships(state.customer.id, true, source.token);
  }

  async function handleMoveMembership() {
    const moveMembershipRes = await MoveMembership(
      {
        customer_membership_id: moveState.membershipId,
        new_customer_id: moveState.selectedCustomer.id,
      },
      true,
    );

    if (moveMembershipRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error moving membership")));
      return;
    }

    updateMoveState(prevState => ({
      ...prevState,
      showMoveMembership: false,
      membershipId: null,
      showWarning: false,
      selectedCustomer: null,
      customerQuery: "",
    }));

    updateState({
      customer: { ...state.customer, ...moveMembershipRes.data.customer },
    });

    const source = axios.CancelToken.source();
    void loadCustomerMemberships(state.customer.id, true, source.token);
  }

  function handleMoveMembershipPopup(membership_id: number) {
    updateMoveState(prevState => ({ ...prevState, showMoveMembership: true, membershipId: membership_id }));
  }

  function changePlayerHandleCustomerSearch(query: string) {
    updateMoveState(prevState => ({ ...prevState, customerQuery: query }));
  }

  function handleChangePlayerSelection(id: number, customer: ICustomer) {
    updateMoveState(prevState => ({ ...prevState, selectedCustomer: customer }));
  }

  function handleSuspendToggle(event: React.ChangeEvent<HTMLInputElement>) {
    const status = event.target.checked;
    updateSuspendState(prev => ({ ...prev, indefinite_suspension: status }));
  }

  function handleCancelMoveMembership() {
    updateMoveState(prevState => ({
      ...prevState,
      showMoveMembership: false,
      membershipId: null,
      showWarning: false,
      selectedCustomer: null,
      customerQuery: "",
    }));
  }

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    if (mounted === true) {
      timeoutId = global.setTimeout(() => {
        void changePlayerSearch(mounted, moveState.customerQuery);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateMoveState(prevState => ({ ...prevState, playerSearchResult: [] }));
    };
  }, [moveState.customerQuery]);

  async function changePlayerSearch(mounted: boolean, customerSearchQuery: string) {
    try {
      if (customerSearchQuery === "") {
        if (mounted) {
          updateMoveState(prevState => ({ ...prevState, customerSearchResult: [] }));
        }
        return;
      } else {
        updateMoveState(prevState => ({ ...prevState, customerSearching: true }));
        const customerRes = await GetCustomer({ search: customerSearchQuery }, false);
        if (customerRes.status !== StatusCode.OK) {
          updateMoveState(prevState => ({ ...prevState, customerSearching: false, customerSearchResult: [] }));
          return;
        } else if (mounted) {
          updateMoveState(prevState => ({
            ...prevState,
            customerSearching: false,
            customerSearchResult: customerRes.data,
          }));
        }
      }
    } catch (error) {
      console.log("err", error);
    }
    return;
  }

  async function handlePrintMembership(membership: CustomerMembershipsType) {
    const params = {
      id: membership.id,
    };

    const printRes = await PrintMembership(params, true);

    if (printRes.status === StatusCode.OK) {
      window.open().document.write(String(printRes.data));
    } else {
      dispatch(showError(t("Error printing membership")));
    }
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    updateSuspendState(prev => ({ ...prev, suspension_reason: event.target.value }));
  }

  async function handleEmailMembership(membership: CustomerMembershipsType) {
    const params = {
      id: membership.id,
    };

    const emailRes = await EmailMembership(params, true);

    if (emailRes.status === StatusCode.OK) {
      dispatch(
        showSuccess(t("Membership card e-mailed successfully to ") + String(membership.customer?.full_name) + "."),
      ); //TODO: Translate
    } else {
      dispatch(showError(t("Error emailing membership")));
    }
  }

  async function handleEnableMembership() {
    const enableRes = await EnableMembership({ membership_id: enableState.membershipId }, true);

    if (enableRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error enabling membership")));
      return;
    }

    dispatch(showSuccess(t("Membership enabled")));
    updateEnabledState({ showEnableMembership: false, membershipId: null });
    void loadCustomerMemberships(state.customer.id, true);
  }

  async function handleEditExpiryDate() {
    const params = {
      membership_id: editExpiryDateState.membership.id,
      expiry: editExpiryDateState.membership.expiry,
    };

    const editExpiryDateRes = await PutEditExpiryDate(params, true);
    if (editExpiryDateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error editing expiry date")));
      return;
    }
    updateEditExpiryDateState(prev => ({ ...prev, modalOpen: false, membership: null }));
    dispatch(showSuccess(t("Expiry date edited successfully")));
    void loadCustomerMemberships(state.customer.id, true);
  }

  /** Handles -- MembershipDisplay || Empty Card || Spinner -- rendering */
  const renderMembershipCards = (memberships: CustomerMembershipsType[], expired: boolean) => {
    return (
      <>
        {memberships ? (
          <>
            {memberships.length > 0 ? (
              memberships.map(membership => {
                return (
                  <div key={membership.id.toString() + "_active"} style={{ marginBottom: "14px" }}>
                    <MembershipDisplay
                      id={membership.id}
                      title={membership.membership_title}
                      subtitle={membership.membership_subtitle}
                      type={membership.membership_type}
                      expiry={membership.expiry}
                      suspension_start_date={membership.suspension_start_date}
                      suspension_end_date={membership?.status === "suspended" ? membership.suspension_end_date : null}
                      indefinite_suspension={membership?.indefinite_suspension}
                      logo_image_source={membership.parent_membership?.logo_image_source}
                      status={membership.status}
                      status_readable={membership.status_readable}
                      suspension_reason={membership.suspension_reason}
                      suspendMembership={() => {
                        updateSuspendState(prevState => ({
                          ...prevState,
                          indefinite_suspension: membership.indefinite_suspension,
                          suspension_reason: membership.suspension_reason,
                          showSuspendMembership: true,
                          membership: membership,
                        }));
                        if (membership.suspension_start_date) {
                          setDate(new Date(`${String(membership.suspension_start_date)}T00:00`));
                        }
                        if (membership.suspension_end_date) {
                          setEndDate(new Date(`${String(membership.suspension_end_date)}T00:00`));
                        }
                      }}
                      unsuspendMembership={() =>
                        setUnsuspendState(prevState => ({ ...prevState, modalOpen: true, membership: membership }))
                      }
                      disableMembership={() =>
                        updateDisableState(prev => ({
                          ...prev,
                          showDisableMembership: true,
                          membershipId: membership.id,
                        }))
                      }
                      enableMembership={() =>
                        updateEnabledState(prev => ({
                          ...prev,
                          showEnableMembership: true,
                          membershipId: membership.id,
                        }))
                      }
                      deleteMembership={handleDeleteMembershipPopup}
                      moveMembership={handleMoveMembershipPopup}
                      printMembership={void handlePrintMembership}
                      emailMembership={() => void handleEmailMembership(membership)}
                      onClick={() => (!expired ? void setSelectedMembership({ membership, modalOpen: true }) : {})}
                      editExpiryDate={() =>
                        updateEditExpiryDateState(prev => ({ ...prev, modalOpen: true, membership: membership, expiryDate: new Date(membership.expiry) }))
                      }
                    />
                  </div>
                );
              })
            ) : (
              <div key="card_empty" style={{ marginBottom: "14px" }}>
                <MembershipDisplay
                  id={0}
                  title={`${t("This customer does not have any memberships.")}`}
                  subtitle={`${t("Active or expired memberships will appear here.")}`}
                  emptyCard
                />
              </div>
            )}
          </>
        ) : (
          <div className="spinner">
            <span style={{ width: "50px" }}>
              <Spin />
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex">
      <div className="membership-tab-content">
        <div className="membership-tab-content-header">
          <h1 className="ui-customer_page-header">{t("Active Memberships")}</h1>
          <div>
            {!!permissions.memberships_apply_manual && (
              <NewCustomerMembershipModal
                applyMembershipToCustomer={(selectedMembershipId: number) =>
                  void postMembershipToCustomer(state.customer.id, selectedMembershipId)
                }
                adminType="facility"
              >
                {/* onClick handled inside parent */}
                <Button type="secondary" size="medium">
                  <FontAwesomeIcon className="mr-2" icon={["far", "plus"]} />
                  <span>{windowSize.width > MOBILE_WIDTH ? t("Add membership") : t("Add")}</span>
                </Button>
              </NewCustomerMembershipModal>
            )}
          </div>
        </div>

        {renderMembershipCards(activeMemberships, false)}

        <h2 className="ui-customer_page-header customer-memberships-past-memberships">{t("Past Memberships")}</h2>

        {renderMembershipCards(pastMemberships, true)}

        <Sheet
          open={suspendState?.showSuspendMembership}
          title={t("Suspend Membership")}
          supportText={t("Temporarily pause this member's access without cancelling their membership")}
          onOk={() =>
            updateSuspendState(prevState => ({
              ...prevState,
              openConfirmSuspension: true,
              showSuspendMembership: false,
            }))
          }
          overflow
          onCancel={() =>
            updateSuspendState(prevState => ({ ...prevState, showSuspendMembership: false, membership: null }))
          }
        >
          <div className="flex flex-column space-y-7">
            {!suspendState?.indefinite_suspension ? (
              <DatePickerInput
                months={1}
                label={t("Duration")}
                position="left"
                startingDate={date}
                disablePastDays
                setStartingDate={setDate}
                endingDate={endDate}
                setEndingDate={setEndDate}
              />
            ) : null}
            <Input value={suspendState?.suspension_reason} label={t("Notes (Optional)")} onChange={handleInputChange} />

            <Checkbox
              label={t("Suspend Indefinitely")}
              size="large"
              checked={suspendState?.indefinite_suspension}
              onChange={e => handleSuspendToggle(e)}
            />
          </div>
        </Sheet>

        <Popup
          open={suspendState?.openConfirmSuspension}
          type="warning"
          title={t("Are you sure you want to suspend this membership?")}
          description={t("The member will lose access until the suspension is lifted")}
          onOk={handleSuspendMembership}
          onCancel={() =>
            updateSuspendState(prevState => ({
              ...prevState,
              openConfirmSuspension: false,
              showSuspendMembership: true,
            }))
          }
        />
        <Popup
          open={unsuspendState.modalOpen}
          type="warning"
          title={t("Unsuspend Membership")}
          description={t("Allow user membership to be used")}
          onOk={handleUnsuspendMembership}
          okText={t("Resume")}
          onCancel={() => setUnsuspendState(prev => ({ ...prev, openModal: false }))}
          cancelText={t("Cancel")}
        />

        <Popup
          open={disableState.showDisableMembership}
          type="warning"
          title={t("Disable Membership?")}
          description={t("Are you sure you want to disable this membership? This cannot be undone")}
          onOk={handleDisableMembership}
          okText={t("Disable")}
          cancelText={t("Cancel")}
          onCancel={() =>
            updateDisableState(prevState => ({ ...prevState, showDisableMembership: false, membershipId: null }))
          }
        />

        <Popup
          open={deleteState.showDeleteMembership}
          type="warning"
          title={t("Delete Membership")}
          description={t("Are you sure you want to delete this membership? This cannot be undone")}
          onOk={handleDeleteMembership}
          okText={t("Delete")}
          cancelText={t("Cancel")}
          onCancel={() =>
            updateDeleteState(prevState => ({
              ...prevState,
              showDeleteMembership: false,
              membershipIdToDelete: null,
            }))
          }
        />

        <Popup
          open={enableState.showEnableMembership}
          type="warning"
          title={t("Enable Membership")}
          description={t("Are you sure you want to re-enable this membership?")}
          onOk={handleEnableMembership}
          okText={t("Enable")}
          cancelText={t("Cancel")}
          onCancel={() => {
            updateEnabledState(prevState => ({ ...prevState, showEnableMembership: false, membershipId: null }));
          }}
        />

        <Sheet
          title={t("Move Membership")}
          open={moveState.showMoveMembership}
          size="small"
          closable
          onCancel={handleCancelMoveMembership}
          onOk={() => updateMoveState(prevState => ({ ...prevState, showWarning: true, showMoveMembership: false }))}
          okText={t("Move")}
          okDisabled={moveState.selectedCustomer ? false : true}
          overflow
        >
          {moveState.currentCustomer !== null ? (
            <div>
              <span>{t("secure.facility.order.order.035")}</span>

              <GolferCard
                email={moveState.currentCustomer?.email}
                name={moveState.currentCustomer?.full_name}
                memberCode={moveState.currentCustomer?.member_code}
                customerType={moveState.currentCustomer?.customer_type}
                phone={moveState.currentCustomer?.phone}
              />

              <FontAwesomeIcon
                className="ml-auto mr-auto mt-4 mb-2 block text-primary-ui-colour"
                icon={["far", "arrow-circle-down"]}
                size="2x"
              />
            </div>
          ) : null}

          <span>{t("secure.facility.order.order.036")}</span>
          {moveState.selectedCustomer ? (
            <GolferCard
              closable
              removeGolfer={() =>
                updateMoveState(prevState => ({
                  ...prevState,
                  selectedCustomer: null,
                  playerSearchResult: [],
                  customerQuery: "",
                }))
              }
              email={moveState.selectedCustomer.email}
              name={moveState.selectedCustomer.full_name}
              memberCode={moveState.selectedCustomer.member_code}
              customerType={moveState.selectedCustomer.customer_type}
              phone={moveState.selectedCustomer.phone}
            />
          ) : (
            <Select
              showSearch
              className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
              onSearch={(query: string) => changePlayerHandleCustomerSearch(query)}
              onChange={(id: number, customer: ICustomer) => handleChangePlayerSelection(id, customer)}
              placeholder={t("secure.facility.order.order.037")}
              allowClear
              searchValue={moveState.customerQuery}
              showDropDownOnFocus={true}
              searching={moveState.customerSearching}
            >
              <></>
              {moveState.customerSearchResult?.map((customer, index) => {
                return (
                  <Option key={index} value={customer.id} name={customer.full_name} extraValues={customer}>
                    <div className="flex justify-between">
                      <div>
                        <div className="text-semibold text-lg">{customer?.full_name}</div>
                        <div className="text-sm text-gray-500">{customer.customer_type}</div>
                        <div className="text-sm text-gray-500">{customer.email}</div>
                        <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                      </div>

                      <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
                    </div>
                  </Option>
                );
              })}
            </Select>
          )}
        </Sheet>

        <Popup
          open={moveState.showWarning}
          type="warning"
          title={t("Move Membership?")}
          description={t("Are you sure you want to move this membership? This will reset the current customer's type")}
          onOk={handleMoveMembership}
          okText={t("Move")}
          cancelText={t("Cancel")}
          onCancel={handleCancelMoveMembership}
        />

        <MembershipDetailsSheet
          open={selectedMembership?.modalOpen}
          enableEmailAction={false}
          cardImageSrc={selectedMembership?.membership?.parent_membership?.card_image_source}
          fullName={state.customer?.full_name}
          season={selectedMembership?.membership?.season}
          membershipType={selectedMembership?.membership?.membership_type}
          expiry={selectedMembership?.membership?.expiry}
          membershipTitle={selectedMembership?.membership?.membership_title}
          membershipSubtitle={selectedMembership?.membership?.membership_subtitle}
          memberCode={selectedMembership?.membership?.customer?.member_code}
          onCancel={() => setSelectedMembership({ membership: null, modalOpen: false })}
        />

        <Sheet
          open={editExpiryDateState.modalOpen}
          size="small"
          title={t("Edit Expiry Date")}
          supportText={t("Please select a new expiry date for this membership")}
          onOk={handleEditExpiryDate}
          okText={t("Save")}
          cancelText={t("Cancel")}
          onCancel={() => updateEditExpiryDateState(prev => ({ ...prev, modalOpen: false, membership: null }))}
          overflow
        >
          <div className="mt-2">
            <DatePickerInput
              showQuickOptions
              months={1}
              label={t("Expiry Date")}
              position="left"
              startingDate={
                editExpiryDateState.expiryDate
              }
              setStartingDate={(date: Date) =>
                updateEditExpiryDateState(prev => ({
                  ...prev,
                  membership: { ...prev.membership, expiry: formatDate(date) },
                }))
              }
            />
          </div>
        </Sheet>
      </div>
      {futureBookingPricingModal}
    </div>
  );
}
